import { useMemo, useState, type ReactElement } from "react";
import { Building2, Plus, Search } from "lucide-react";
import { Badge, Button, EmptyState, Stack, Text, TextInput } from "@ux/primitives";
import { BottomActionBar, ConfirmAction, SplitPane, Toolbar } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { useApiClient } from "@shell/apiClientContext";
import { describeApiError } from "../common/errors";
import { ExportDataButton } from "../common/ExportDataButton";
import { buildSageOfficesExportCsv } from "../common/exportCsv";
import { SageOfficesList } from "./SageOfficesList";
import { SageOfficeForm, type SageOfficeFormMode } from "./SageOfficeForm";
import {
    useCreateSageOffice,
    useDeleteSageOffice,
    useRestoreSageOffice,
    useSageOffices,
    useUpdateSageOffice,
} from "./hooks";
import { exportSageOffices } from "./api";

type Mode = { kind: "idle" } | { kind: "create" } | { kind: "edit"; id: string };

export function SageOfficesPage(): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();
    const client = useApiClient();
    const [search, setSearch] = useState("");
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [mode, setMode] = useState<Mode>({ kind: "idle" });
    const [pendingDelete, setPendingDelete] = useState<string | null>(null);
    const [pendingRestore, setPendingRestore] = useState<string | null>(null);

    const officesQuery = useSageOffices({ q: search.trim(), includeDeleted });
    const createMutation = useCreateSageOffice();
    const updateMutation = useUpdateSageOffice();
    const deleteMutation = useDeleteSageOffice();
    const restoreMutation = useRestoreSageOffice();

    const rows = useMemo(() => officesQuery.data ?? [], [officesQuery.data]);

    const selectedRecord = useMemo(() => {
        if (mode.kind !== "edit") return null;
        return rows.find((r) => r.id === mode.id) ?? null;
    }, [mode, rows]);

    const formMode: SageOfficeFormMode | null = useMemo(() => {
        if (mode.kind === "create") return { kind: "create" };
        if (mode.kind === "edit" && selectedRecord !== null)
            return { kind: "edit", record: selectedRecord };
        return null;
    }, [mode, selectedRecord]);

    const isSaving = createMutation.isPending || updateMutation.isPending;
    const formError =
        mode.kind === "create" && createMutation.isError
            ? describeApiError(createMutation.error)
            : mode.kind === "edit" && updateMutation.isError
              ? describeApiError(updateMutation.error)
              : null;

    function handleSave(payload: Parameters<typeof createMutation.mutate>[0]): void {
        // Post-save → idle (back to list). See AssetsPage.handleSave for
        // the rationale; same rule applies uniformly across admin pages.
        if (mode.kind === "create") {
            createMutation.mutate(payload, {
                onSuccess: () => setMode({ kind: "idle" }),
            });
        } else if (mode.kind === "edit") {
            updateMutation.mutate(payload, {
                onSuccess: () => setMode({ kind: "idle" }),
            });
        }
    }

    function handleDeleteConfirmed(): void {
        if (pendingDelete === null) return;
        const id = pendingDelete;
        deleteMutation.mutate(id, {
            onSuccess: () => {
                setPendingDelete(null);
                setMode({ kind: "idle" });
            },
        });
    }

    function handleRestoreConfirmed(): void {
        if (pendingRestore === null) return;
        restoreMutation.mutate(pendingRestore, {
            onSuccess: () => {
                setPendingRestore(null);
                setMode({ kind: "idle" });
            },
        });
    }

    return (
        <Stack direction="column" gap={0} grow style={{ overflow: "hidden" }}>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">Sage Offices</Text>
                        <Badge tone="neutral">{rows.length}</Badge>
                    </Stack>
                }
                trailing={
                    <Stack direction="row" gap={2} align="center" wrap>
                        <Search size={14} style={{ opacity: 0.55 }} />
                        <div style={{ width: 220 }}>
                            <TextInput
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name…"
                            />
                        </div>
                        <Button
                            variant={includeDeleted ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => setIncludeDeleted((v) => !v)}
                        >
                            {includeDeleted ? "Showing deleted" : "Show deleted"}
                        </Button>
                        <ExportDataButton
                            filenamePrefix="sage-offices"
                            run={async () => {
                                const exported = await exportSageOffices(client, {
                                    q: search.trim(),
                                    includeDeleted,
                                });
                                return {
                                    csv: buildSageOfficesExportCsv(exported.results),
                                    truncated: exported.truncated,
                                    rowCount: exported.results.length,
                                };
                            }}
                        />
                        {!isPhone && canEdit ? (
                            <Button
                                variant="primary"
                                size="sm"
                                leadingIcon={<Plus size={16} />}
                                onClick={() => setMode({ kind: "create" })}
                            >
                                New office
                            </Button>
                        ) : null}
                    </Stack>
                }
            />

            <SplitPane
                activeBelowDesktop={mode.kind === "idle" ? "master" : "detail"}
                master={
                    <SageOfficesList
                        rows={rows}
                        selectedId={mode.kind === "edit" ? mode.id : null}
                        onSelect={(row) => setMode({ kind: "edit", id: row.id })}
                        isLoading={officesQuery.isPending}
                        error={officesQuery.isError ? describeApiError(officesQuery.error) : null}
                    />
                }
                detail={
                    formMode === null ? (
                        <EmptyState
                            icon={<Building2 size={32} />}
                            title={
                                canEdit ? "Select an office to edit" : "Select an office to view"
                            }
                            description={
                                canEdit
                                    ? "Pick a row on the left, or create a new office."
                                    : "Pick a row on the left."
                            }
                            action={
                                canEdit ? (
                                    <Button
                                        variant="primary"
                                        leadingIcon={<Plus size={16} />}
                                        onClick={() => setMode({ kind: "create" })}
                                    >
                                        New office
                                    </Button>
                                ) : undefined
                            }
                        />
                    ) : (
                        <SageOfficeForm
                            mode={formMode}
                            onSave={handleSave}
                            onCancel={() => setMode({ kind: "idle" })}
                            onRequestDelete={(id) => setPendingDelete(id)}
                            onRequestRestore={(id) => setPendingRestore(id)}
                            isSaving={isSaving}
                            errorMessage={formError}
                        />
                    )
                }
            />

            {isPhone && canEdit && mode.kind === "idle" ? (
                <BottomActionBar>
                    <Button
                        variant="primary"
                        leadingIcon={<Plus size={16} />}
                        onClick={() => setMode({ kind: "create" })}
                    >
                        New office
                    </Button>
                </BottomActionBar>
            ) : null}

            <ConfirmAction
                open={pendingDelete !== null}
                title="Delete sage office?"
                description="The record will be marked deleted and hidden from default lists. You can restore it later."
                confirmLabel="Delete"
                destructive
                busy={deleteMutation.isPending}
                onCancel={() => setPendingDelete(null)}
                onConfirm={handleDeleteConfirmed}
            />
            <ConfirmAction
                open={pendingRestore !== null}
                title="Restore sage office?"
                description="The office will become active again."
                confirmLabel="Restore"
                busy={restoreMutation.isPending}
                onCancel={() => setPendingRestore(null)}
                onConfirm={handleRestoreConfirmed}
            />
        </Stack>
    );
}
